import * as RadixDialog from "@radix-ui/react-dialog";
import * as RadixPopover from "@radix-ui/react-popover";
import {
  ArrowCounterClockwise,
  BookmarkSimple,
  CaretDown,
  DotsThreeVertical,
  PencilSimple,
  Star,
  Trash
} from "@phosphor-icons/react";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type MutableRefObject, type ReactNode } from "react";
import styled from "@emotion/styled";
import { ActionMenu } from "@/components/navigation/ActionMenu";
import { Button, IconButton } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { kfContentIn, kfModalContentIn, kfOverlayIn, twRing, twTransform } from "@/lib/emotionTw";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import type { SavedView } from "./DataTable.types";
import { createTableViewSelectorStore, type ViewStore } from "./viewStore";
import type { SaveViewApiConfig } from "./saveViewApi";

export type TableViewSelectorProps = {
  /** Free-form screen key used for localStorage scoping, e.g. "orders_new". */
  screenName: string;
  /** Optional store adapter. Defaults to the component-owned local/API store for this screen. */
  store?: ViewStore;
  /** Optional API adapter config for the default local store. Ignored when `store` is supplied. */
  saveApi?: SaveViewApiConfig;
  /** Controlled selected view id. Selection is separate from the persisted primary view. */
  value?: string | null;
  /** Initial selected view id for uncontrolled mode. Falls back to the store's primary view. */
  defaultValue?: string | null;
  /** Select and emit the screen's primary view when no selected value is present. Defaults to true. */
  selectPrimaryByDefault?: boolean;
  onValueChange?: (viewId: string | null, view: SavedView | null) => void;
  onViewSelect?: (view: SavedView) => void;
  onPrimaryViewChange?: (view: SavedView) => void;
  onRenameView?: (view: SavedView) => void;
  onDeleteView?: (viewId: string) => void;
  onResetSavedFilters?: () => void;
  onError?: (error: unknown) => void;
  triggerLabel?: ReactNode;
  placeholder?: ReactNode;
  viewsLabel?: ReactNode;
  emptyLabel?: ReactNode;
  loadingLabel?: ReactNode;
  resetLabel?: ReactNode;
  setPrimaryLabel?: ReactNode;
  renameLabel?: ReactNode;
  deleteLabel?: ReactNode;
  renameTitle?: ReactNode;
  renameDescription?: ReactNode;
  renameInputLabel?: ReactNode;
  renameCancelLabel?: ReactNode;
  renameSaveLabel?: ReactNode;
  /** Trigger size. Matches select-family sizing: sm = 28px, md = 36px. */
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  actionMenuClassName?: string;
};

const textPrimary = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
};

const textSecondary = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
};

const textMuted = {
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
};

const bgCard = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
};

const bgSoft = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
};

const borderDefault = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
};

const ViewSelectorRoot = styled("div", {
  label: "AgenticTableViewSelector-root"
})({
  display: "inline-flex",
  minWidth: "0px"
});

const ViewSelectorTrigger = styled("button", {
  label: "AgenticTableViewSelector-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean; empty: boolean; size: "sm" | "md" } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    minWidth: "0px",
    maxWidth: "12rem",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ".5rem",
    borderRadius: "7px",
    borderWidth: "1px",
    ...bgCard,
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: "12px",
    lineHeight: "normal",
    fontWeight: 400,
    ...textPrimary,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(0,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": bgSoft,
    "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
    "&:disabled": {
      pointerEvents: "none",
      opacity: ".55"
    }
  },
  ownerState.size === "sm" ? { height: "1.75rem" } : { height: "2.25rem" },
  ownerState.open
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
      }
    : borderDefault,
  ownerState.empty && textMuted
]);

const ViewSelectorTriggerLabel = styled("span", {
  label: "AgenticTableViewSelector-triggerLabel"
})({
  minWidth: "0px",
  flex: "0 1 auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const ViewSelectorPrimaryStar = styled(Star, {
  label: "AgenticTableViewSelector-primaryStar"
})({
  flexShrink: 0,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-warning) / var(--tw-text-opacity, 1))"
});

const ViewSelectorCaret = styled(CaretDown, {
  label: "AgenticTableViewSelector-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    ...textMuted,
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const ViewSelectorContent = styled(RadixPopover.Content, {
  label: "AgenticTableViewSelector-content"
})({
  zIndex: 1000,
  width: "min(calc(100vw - 1rem),16rem)",
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  ...borderDefault,
  ...bgCard,
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
});

const ViewSelectorHeader = styled("div", {
  label: "AgenticTableViewSelector-header"
})({
  paddingLeft: ".75rem",
  paddingRight: "1rem",
  paddingTop: ".625rem",
  paddingBottom: ".375rem",
  fontSize: "10px",
  lineHeight: "1rem",
  fontWeight: 700,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  ...textMuted
});

const ViewSelectorList = styled("div", {
  label: "AgenticTableViewSelector-list"
})({
  display: "flex",
  maxHeight: "14rem",
  flexDirection: "column",
  overflowY: "auto",
  paddingBottom: ".25rem"
});

const ViewSelectorRow = styled("div", {
  label: "AgenticTableViewSelector-row"
})({
  display: "flex",
  minHeight: "2.25rem",
  alignItems: "center",
  gap: ".375rem",
  paddingLeft: ".5rem",
  paddingRight: ".625rem"
});

const ViewSelectorViewButton = styled("button", {
  label: "AgenticTableViewSelector-viewButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { selected: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    minWidth: "0px",
    flex: "1 1 0%",
    alignItems: "center",
    gap: ".5rem",
    minHeight: "1.75rem",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    paddingTop: ".375rem",
    paddingBottom: ".375rem",
    textAlign: "left",
    fontSize: "12px",
    lineHeight: "1rem",
    fontWeight: 500,
    ...textPrimary,
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": textSecondary,
    "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)")
  },
  ownerState.selected && {
    fontWeight: 700
  }
]);

const ViewSelectorName = styled("span", {
  label: "AgenticTableViewSelector-name"
})({
  minWidth: "0px",
  flex: "0 1 auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const ViewSelectorDivider = styled("div", {
  label: "AgenticTableViewSelector-divider"
})({
  height: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-border-light) / var(--tw-bg-opacity, 1))"
});

const ViewSelectorResetButton = styled("button", {
  label: "AgenticTableViewSelector-resetButton"
})({
  display: "flex",
  width: "100%",
  minHeight: "2.25rem",
  alignItems: "center",
  gap: ".5rem",
  paddingLeft: ".75rem",
  paddingRight: "1rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 500,
  ...textSecondary,
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:hover": bgSoft,
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)")
});

const ViewSelectorEmpty = styled("div", {
  label: "AgenticTableViewSelector-empty"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".375rem",
  paddingBottom: ".75rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  ...textMuted
});

const RenameOverlay = styled(RadixDialog.Overlay, {
  label: "AgenticTableViewSelector-renameOverlay"
})({
  position: "fixed",
  inset: 0,
  zIndex: 1050,
  backgroundColor: "hsl(var(--bfrs-color-text-primary) / .45)",
  ...kfOverlayIn,
  animation: "bfrs-bfrs-overlay-in var(--bfrs-motion-fast) var(--bfrs-ease-out)"
});

const RenameContent = styled(RadixDialog.Content, {
  label: "AgenticTableViewSelector-renameContent"
})({
  position: "fixed",
  left: "50%",
  top: "50%",
  zIndex: 1050,
  width: "calc(100% - 2rem)",
  maxWidth: "420px",
  maxHeight: "calc(100dvh - 2rem)",
  overflow: "auto",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  ...borderDefault,
  ...bgCard,
  padding: "1.25rem",
  ...textPrimary,
  "--tw-shadow": "var(--bfrs-shadow-overlay)",
  "--tw-shadow-colored": "var(--bfrs-shadow-overlay)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  ...twTransform({ "--tw-translate-x": "-50%", "--tw-translate-y": "-50%" }),
  ...kfModalContentIn,
  animation: "bfrs-bfrs-modal-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
});

const RenameTitle = styled(RadixDialog.Title, {
  label: "AgenticTableViewSelector-renameTitle"
})({
  fontSize: "1.125rem",
  lineHeight: "1.5rem",
  fontWeight: 700,
  ...textPrimary
});

const RenameDescription = styled(RadixDialog.Description, {
  label: "AgenticTableViewSelector-renameDescription"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  ...textMuted
});

const RenameLabel = styled("label", {
  label: "AgenticTableViewSelector-renameLabel"
})({
  display: "block",
  marginTop: "1.25rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 700,
  ...textPrimary
});

const RenameInput = styled("input", {
  label: "AgenticTableViewSelector-renameInput"
})({
  marginTop: ".5rem",
  height: "2.5rem",
  width: "100%",
  borderRadius: "var(--bfrs-radius-md)",
  borderWidth: "1.5px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
  ...bgCard,
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 500,
  ...textPrimary,
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)")
});

const RenameFooter = styled("div", {
  label: "AgenticTableViewSelector-renameFooter"
})({
  marginTop: "1.5rem",
  display: "flex",
  justifyContent: "flex-end",
  gap: ".625rem"
});

function viewById(views: SavedView[], viewId?: string | null) {
  if (!viewId) return null;
  return views.find((view) => view.view_id === viewId) ?? null;
}

function orderSelectorViews(views: SavedView[], primaryViewId?: string | null, selectedViewId?: string | null) {
  const ordered: SavedView[] = [];
  const used = new Set<string>();
  const fallbackPrimaryViewId = primaryViewId ?? views.find((view) => view.is_default)?.view_id ?? null;

  const pushById = (viewId?: string | null) => {
    if (!viewId || used.has(viewId)) return;
    const view = viewById(views, viewId);
    if (!view) return;
    ordered.push(view);
    used.add(view.view_id);
  };

  pushById(fallbackPrimaryViewId);
  pushById(selectedViewId);

  for (const view of views) {
    if (!used.has(view.view_id)) {
      ordered.push(view);
      used.add(view.view_id);
    }
  }

  return ordered;
}

function saveApiCapabilityKey(saveApi: SaveViewApiConfig | undefined) {
  if (!saveApi) return "none";
  return [
    "api",
    saveApi.viewType && saveApi.tab ? "scoped" : "local",
    saveApi.saveEndpoint || saveApi.endpoint ? "remote-save" : "local-save",
  ].join(":");
}

function createLiveSaveApiConfig(saveApiRef: MutableRefObject<SaveViewApiConfig | undefined>): SaveViewApiConfig {
  const current = () => saveApiRef.current;
  return {
    get baseUrl() {
      return current()?.baseUrl;
    },
    get endpoint() {
      return current()?.endpoint;
    },
    get saveEndpoint() {
      return current()?.saveEndpoint;
    },
    get listEndpoint() {
      return current()?.listEndpoint;
    },
    get getEndpoint() {
      return current()?.getEndpoint;
    },
    get updateEndpoint() {
      return current()?.updateEndpoint;
    },
    get deleteEndpoint() {
      return current()?.deleteEndpoint;
    },
    get viewType() {
      return current()?.viewType;
    },
    get tab() {
      return current()?.tab;
    },
    get token() {
      return current()?.token;
    },
    get headers() {
      return current()?.headers;
    },
    get credentials() {
      return current()?.credentials;
    },
    get fetcher() {
      return current()?.fetcher;
    },
  };
}

export function TableViewSelector({
  screenName,
  store,
  saveApi,
  value,
  defaultValue,
  selectPrimaryByDefault = true,
  onValueChange,
  onViewSelect,
  onPrimaryViewChange,
  onRenameView,
  onDeleteView,
  onResetSavedFilters,
  onError,
  triggerLabel,
  placeholder = "Views",
  viewsLabel = "VIEWS",
  emptyLabel = "No saved views",
  loadingLabel = "Loading views",
  resetLabel = "Switch to default",
  setPrimaryLabel = "Set as Primary View",
  renameLabel = "Rename",
  deleteLabel = "Delete",
  renameTitle = "Rename view",
  renameDescription = "Update the saved view name.",
  renameInputLabel = "View name",
  renameCancelLabel = "Cancel",
  renameSaveLabel = "Save",
  size = "sm",
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  actionMenuClassName
}: TableViewSelectorProps) {
  const saveApiRef = useRef(saveApi);
  saveApiRef.current = saveApi;
  const saveApiKey = saveApiCapabilityKey(saveApi);
  const stableSaveApi = useMemo(
    () => (saveApiKey === "none" ? undefined : createLiveSaveApiConfig(saveApiRef)),
    [saveApiKey]
  );
  const resolvedStore = useMemo(
    () => store ?? createTableViewSelectorStore(screenName, stableSaveApi ? { saveApi: stableSaveApi } : undefined),
    [screenName, store, stableSaveApi]
  );
  const isControlled = value !== undefined;
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[]>([]);
  const [primaryViewId, setPrimaryViewId] = useState<string | null>(null);
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue ?? null);
  const [loading, setLoading] = useState(false);
  const [renamingView, setRenamingView] = useState<SavedView | null>(null);
  const [renameName, setRenameName] = useState("");
  const activeValue = value ?? internalValue;
  const activeValueRef = useRef(activeValue);
  const isControlledRef = useRef(isControlled);
  const primaryDefaultAppliedRef = useRef(false);
  const primaryDefaultSuppressedRef = useRef(false);
  const loadInFlightRef = useRef<{ key: string; promise: Promise<SavedView | null> } | null>(null);
  const renameInputId = useId();

  useEffect(() => {
    activeValueRef.current = activeValue;
    if (activeValue) {
      primaryDefaultSuppressedRef.current = false;
    }
  }, [activeValue]);

  useEffect(() => {
    isControlledRef.current = isControlled;
  }, [isControlled]);

  useEffect(() => {
    loadInFlightRef.current = null;
    primaryDefaultAppliedRef.current = false;
    primaryDefaultSuppressedRef.current = false;
  }, [resolvedStore, screenName]);

  const reportError = useCallback(
    (error: unknown) => {
      onError?.(error);
    },
    [onError]
  );

  const selectView = useCallback(
    (view: SavedView | null) => {
      if (view) {
        primaryDefaultSuppressedRef.current = false;
      }
      if (!isControlledRef.current) {
        setInternalValue(view?.view_id ?? null);
      }
      onValueChange?.(view?.view_id ?? null, view);
      if (view) onViewSelect?.(view);
    },
    [onValueChange, onViewSelect]
  );

  const loadViews = useCallback(
    async (preferredViewId?: string | null) => {
      const loadKey = preferredViewId === undefined ? "__default__" : preferredViewId ?? "__none__";
      if (loadInFlightRef.current?.key === loadKey) return loadInFlightRef.current.promise;

      const request = { key: loadKey, promise: Promise.resolve(null) as Promise<SavedView | null> };
      loadInFlightRef.current = request;
      request.promise = (async () => {
        setLoading(true);
        try {
          const nextViews = await resolvedStore.list();
          const primaryView = nextViews.find((view) => view.is_default) ?? null;
          const requestedViewId = preferredViewId !== undefined ? preferredViewId : activeValueRef.current;
          const selectedFromValue = viewById(nextViews, requestedViewId);
          const allowPrimaryFallback =
            !primaryDefaultSuppressedRef.current &&
            (preferredViewId !== undefined || selectPrimaryByDefault || !isControlledRef.current);
          const selectedFromPrimary = selectedFromValue || !allowPrimaryFallback ? null : viewById(nextViews, primaryView?.view_id);
          const selected = selectedFromValue ?? selectedFromPrimary ?? null;

          setViews(nextViews);
          setPrimaryViewId(primaryView?.view_id ?? null);
          if (!isControlledRef.current) {
            setInternalValue(selected?.view_id ?? null);
          }
          if (
            selectPrimaryByDefault &&
            selectedFromPrimary &&
            preferredViewId === undefined &&
            !activeValueRef.current &&
            !primaryDefaultAppliedRef.current
          ) {
            primaryDefaultAppliedRef.current = true;
            selectView(selectedFromPrimary);
          }
          return selected;
        } catch (error) {
          reportError(error);
          return null;
        } finally {
          setLoading(false);
          if (loadInFlightRef.current === request) {
            loadInFlightRef.current = null;
          }
        }
      })();
      return request.promise;
    },
    [reportError, resolvedStore, selectPrimaryByDefault, selectView]
  );

  useEffect(() => {
    const refresh = () => {
      void loadViews();
    };

    refresh();
    const unsubscribe = resolvedStore.subscribe(refresh);
    return unsubscribe;
  }, [loadViews, resolvedStore]);

  const selectedView =
    viewById(views, activeValue) ??
    (!primaryDefaultSuppressedRef.current && (selectPrimaryByDefault || !isControlled)
      ? viewById(views, primaryViewId)
      : null);
  const orderedViews = useMemo(
    () => orderSelectorViews(views, primaryViewId, selectedView?.view_id),
    [primaryViewId, selectedView?.view_id, views]
  );
  const displayLabel = triggerLabel ?? selectedView?.name ?? placeholder;
  const hasViews = views.length > 0;

  const handleSelectView = (view: SavedView) => {
    selectView(view);
    setOpen(false);
  };

  const handleSetPrimary = (view: SavedView) => {
    void resolvedStore
      .setPrimary(view.view_id)
      .then(async (updatedView) => {
        const selected = await loadViews(view.view_id);
        selectView(selected ?? updatedView);
        onPrimaryViewChange?.(updatedView);
      })
      .catch(reportError);
  };

  const handleDelete = (view: SavedView) => {
    const wasSelected = activeValueRef.current === view.view_id;
    void resolvedStore
      .remove(view.view_id)
      .then(async () => {
        const selected = await loadViews(wasSelected ? null : activeValueRef.current);
        if (wasSelected) {
          selectView(selected);
        }
        onDeleteView?.(view.view_id);
      })
      .catch(reportError);
  };

  const handleReset = () => {
    primaryDefaultSuppressedRef.current = true;
    selectView(null);
    onResetSavedFilters?.();
    setOpen(false);
  };

  const openRename = (view: SavedView) => {
    setRenamingView(view);
    setRenameName(view.name);
  };

  const closeRename = () => {
    setRenamingView(null);
    setRenameName("");
  };

  const handleRenameSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = renameName.trim();
    if (!renamingView || !name) return;

    void resolvedStore
      .rename(renamingView.view_id, name)
      .then(async (updatedView) => {
        await loadViews(renamingView.view_id);
        onRenameView?.(updatedView);
        closeRename();
      })
      .catch(reportError);
  };

  if (!hasViews) {
    return null;
  }

  return (
    <ViewSelectorRoot className={className}>
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <RadixPopover.Trigger asChild>
          <ViewSelectorTrigger
            type="button"
            aria-label="Saved views"
            className={triggerClassName}
            disabled={disabled}
            ownerState={{ open, empty: !selectedView, size }}
          >
            <BookmarkSimple size={13} />
            <ViewSelectorTriggerLabel>{displayLabel}</ViewSelectorTriggerLabel>
            {selectedView?.is_default ? <ViewSelectorPrimaryStar size={13} aria-hidden="true" data-primary-view-indicator="outline" /> : null}
            <ViewSelectorCaret size={13} ownerState={{ open }} />
          </ViewSelectorTrigger>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <ViewSelectorContent
              align="start"
              sideOffset={8}
              collisionPadding={floatingCollisionPadding}
              className={contentClassName}
            >
              <ViewSelectorHeader>{viewsLabel}</ViewSelectorHeader>
              <ViewSelectorList>
                {loading && !hasViews ? <ViewSelectorEmpty>{loadingLabel}</ViewSelectorEmpty> : null}
                {!loading && !hasViews ? <ViewSelectorEmpty>{emptyLabel}</ViewSelectorEmpty> : null}
                {orderedViews.map((view) => {
                  const selected = selectedView?.view_id === view.view_id;

                  return (
                    <ViewSelectorRow key={view.view_id}>
                      <ViewSelectorViewButton type="button" ownerState={{ selected }} onClick={() => handleSelectView(view)}>
                        <ViewSelectorName>{view.name}</ViewSelectorName>
                        {view.is_default ? <ViewSelectorPrimaryStar size={13} aria-hidden="true" data-primary-view-indicator="outline" /> : null}
                      </ViewSelectorViewButton>
                      <ActionMenu
                        align="start"
                        side="right"
                        size="sm"
                        className={actionMenuClassName}
                        trigger={
                          <IconButton
                            variant="secondary"
                            size="sm"
                            icon={<DotsThreeVertical size={14} weight="bold" />}
                            aria-label={`View actions for ${view.name}`}
                          />
                        }
                        items={[
                          ...(!view.is_default
                            ? [
                                {
                                  label: setPrimaryLabel,
                                  icon: <Star size={16} />,
                                  onSelect: () => handleSetPrimary(view)
                                }
                              ]
                            : []),
                          {
                            label: renameLabel,
                            icon: <PencilSimple size={16} />,
                            onSelect: () => openRename(view)
                          },
                          {
                            label: deleteLabel,
                            icon: <Trash size={16} />,
                            destructive: true,
                            onSelect: () => handleDelete(view)
                          }
                        ]}
                      />
                    </ViewSelectorRow>
                  );
                })}
              </ViewSelectorList>
              <ViewSelectorDivider />
              <ViewSelectorResetButton type="button" onClick={handleReset}>
                <ArrowCounterClockwise size={16} />
                <span>{resetLabel}</span>
              </ViewSelectorResetButton>
            </ViewSelectorContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>

      <RadixDialog.Root open={Boolean(renamingView)} onOpenChange={(nextOpen) => {
        if (!nextOpen) closeRename();
      }}>
        <RadixDialog.Portal>
          <BfrsPortalScope>
            <RenameOverlay />
            <RenameContent>
              <form onSubmit={handleRenameSubmit}>
                <RenameTitle>{renameTitle}</RenameTitle>
                {renameDescription ? <RenameDescription>{renameDescription}</RenameDescription> : null}
                <RenameLabel htmlFor={renameInputId}>{renameInputLabel}</RenameLabel>
                <RenameInput
                  id={renameInputId}
                  autoFocus
                  value={renameName}
                  onChange={(event) => setRenameName(event.currentTarget.value)}
                />
                <RenameFooter>
                  <Button type="button" variant="secondary" onClick={closeRename}>
                    {renameCancelLabel}
                  </Button>
                  <Button type="submit" disabled={renameName.trim().length === 0}>
                    {renameSaveLabel}
                  </Button>
                </RenameFooter>
              </form>
            </RenameContent>
          </BfrsPortalScope>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </ViewSelectorRoot>
  );
}
